import { useFpB2bMerchantService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-merchant-service'
import { type CountryCode, Platform, type ProjectCode } from '@constants'
import { useDebounceValue } from '@dni-hooks/useDebounceValue'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useControlled } from '@dni-react-hooks/use-controlled'
import { MuiAutocomplete, TextField, type TextFieldProps } from '@fortune-ui'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import ClearIcon from '@mui/icons-material/Clear'
import { type AutocompleteValue, IconButton, InputAdornment, type UseAutocompleteProps } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useCart } from '../CartProvider/CartProvider'
import { type Option, useAutocompleteMerchantOptions } from './useAutocompleteMerchantOptions'

type AutocompleteProps = UseAutocompleteProps<Option, false, true, false>
type ValueProps = AutocompleteValue<Option, false, true, false>

export interface AutocompleteMerchantProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
  campaignCode?: string
  inputRef?: TextFieldProps['inputRef']
  error?: TextFieldProps['error']
  helperText?: TextFieldProps['helperText']
  onChange?: (value?: Option) => void
  defaultValue?: ValueProps
  value?: ValueProps
}

export const AutocompleteMerchant = (props: AutocompleteMerchantProps) => {
  const { projectCode, countryCode, campaignId, campaignCode, inputRef, error, helperText, onChange, defaultValue: defaultValueProp, value: valueProp } = props
  const { platform } = useCart()
  const { setMerchant, clearMerchant, merchant } = useFpB2bMerchantService(campaignId, platform)
  const defaultValue: ValueProps = defaultValueProp || {
    label: merchant.billingFirstName,
    value: merchant.index,
    billingCountry: merchant.billingCountry,
  }
  const [value, setValue] = useControlled<Option | undefined>({
    controlled: valueProp,
    default: defaultValue,
  })
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const isShopManager = !!userInfo?.roles.includes('shop_manager')
  const [inputValue, setInputValue] = useState(merchant.billingFirstName)
  const debouncedInputValue = useDebounceValue(inputValue, 500)
  const payload = useMemo(() => ({
    isShopManager,
    merchantsSearchRequest: {
      platform: Platform.WORDPRESS, // 經銷商的資料是從 wordpress 來的
      project_code: projectCode,
      query: debouncedInputValue,
      country_code: countryCode,
      user_id: userInfo?.platform_uid,
    },
    partnerProfileRequest: { code: campaignCode },
  }), [isShopManager, projectCode, countryCode, debouncedInputValue, userInfo, campaignCode])
  const options = useAutocompleteMerchantOptions(payload)

  const handleChange: AutocompleteMerchantProps['onChange'] = useCallbackEvent((option) => {
    onChange?.(option)
    setValue(option)

    /**
     * Update merchant when the value is changed
     */
    if (option?.value && option.billingCountry) {
      setMerchant(option.value, option.label, option.billingCountry)
    }
  })

  const handleInputChange: AutocompleteProps['onInputChange'] = (_, value) => {
    setInputValue(value)
  }

  const clearValue = useCallbackEvent(() => {
    setInputValue('')
    setValue(undefined)
    clearMerchant()
  })

  const handleClear = useCallbackEvent(() => {
    clearValue()
    onChange?.()
  })

  /**
   * 如果不是經銷商，且只有一個選項，則自動選擇第一個選項
   */
  useEffect(() => {
    if (!isShopManager && options.length === 1) {
      handleChange(options[0])
    }
  }, [isShopManager, handleChange, options, setValue])

  /**
   * 如果已經選擇過經銷商，但選項中沒有 merchant.index，則清空選項
   */
  useEffect(() => {
    if (!options.length || merchant.index === null) return
    if (!options.find(option => option.value === merchant.index)) {
      clearValue()
    }
  }, [clearValue, merchant.index, options])

  return (
    <MuiAutocomplete
      options={options}
      disableClearable
      disabled={!isShopManager}
      renderInput={({ InputProps, ...params }) => (
        <TextField
          placeholder="請選擇經銷商"
          inputRef={inputRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              'backgroundColor': 'white',
              'height': '38.84px',
              'paddingRight': '9px !important',
              '& .MuiAutocomplete-input': {
                fontSize: '16px',
                padding: '0 0 0 8px !important',
                width: '100%',
              },
            },
          }}
          error={error}
          helperText={helperText}
          InputProps={{
            ...InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClear} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          {...params}
        />
      )}
      defaultValue={defaultValue}
      value={value}
      onInputChange={handleInputChange}
      onChange={(_, value) => handleChange(value)}
    />
  )
}
